import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { uniq } from 'lodash-es';
import {
  FLOW_ROUTES_AND_STEP_NUMBERS,
  FLOWS,
  SEEKER_CHILD_CARE_ROUTES,
  SEEKER_ROUTES,
  CLIENT_FEATURE_FLAGS,
  SEEKER_INSTANT_BOOK_ROUTES,
} from '@/constants';
import { SeekerCCState } from '@/types/seekerCC';
import {
  useFlowState,
  useSeekerCCState,
  useSeekerState,
  useAppDispatch,
} from '@/components/AppState';
import { SENIOR_CARE_TYPE } from '@/__generated__/globalTypes';
import {
  isFromVhp,
  isFromVhpWithZip,
  useRetrieveVhpMfeSharedData,
} from '@/components/hooks/useVhpMfeSharedData';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import { useAdultCare } from '@/components/hooks/useAdultCare';
import { useFeatureFlags } from '../FeatureFlagsContext';

const isDCFlow = (flowName: string) => {
  return (
    flowName === FLOWS.SEEKER_DAYCARE_CHILD_CARE.name ||
    flowName === FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name ||
    flowName === FLOWS.SEEKER_DAYCARE_CARE_KIND.name
  );
};

const findCurrentFlow = (path: string, flowName: string, useReducedFlowTest?: boolean) => {
  if (flowName === FLOWS.SEEKER_DAYCARE_CARE_KIND.name) {
    return flowName;
  }

  if (useReducedFlowTest && isDCFlow(flowName)) {
    return FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name;
  }

  if (Object.keys(FLOW_ROUTES_AND_STEP_NUMBERS[flowName] || {}).includes(path)) {
    return flowName;
  }

  return Object.keys(FLOW_ROUTES_AND_STEP_NUMBERS).find((flow) =>
    Object.keys(FLOW_ROUTES_AND_STEP_NUMBERS[flow]).includes(path)
  );
};

export const getStepsLength = (
  seekerCCState: SeekerCCState,
  flowName: string,
  useReducedFlowTest?: boolean
) => {
  const resolvedFlowName =
    useReducedFlowTest && flowName === FLOWS.SEEKER_DAYCARE_CHILD_CARE.name
      ? FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name
      : flowName;
  if (!FLOW_ROUTES_AND_STEP_NUMBERS[resolvedFlowName]) {
    return 1;
  }

  let count = uniq(Object.values(FLOW_ROUTES_AND_STEP_NUMBERS[resolvedFlowName])).length;

  if (resolvedFlowName === FLOWS.SEEKER_CHILD_CARE.name || isDCFlow(resolvedFlowName)) {
    if (seekerCCState.czenServiceIdForMember) {
      count -= 1;
    }
    if (seekerCCState.czenTimeIntent) {
      count -= 1;
    }
  }

  // reduce total steps by 1 if seeker is coming from SEO and is eligible for skip care kind as we don't count recap in the total steps
  if (resolvedFlowName === FLOWS.SEEKER_CHILD_CARE_SKIP_CARE_KIND_SEO.name) {
    if (seekerCCState.czenServiceIdForMember) count -= 1;
  }

  return count;
};
const getStepNumber = (seekerCCState: SeekerCCState, flowName: string, path: string) => {
  const flow = FLOW_ROUTES_AND_STEP_NUMBERS[flowName];
  if (!flow) {
    return 1;
  }
  let number = flow[path] || 1;
  if (flowName === FLOWS.SEEKER_CHILD_CARE.name) {
    const steps = Object.keys(flow);
    const dateIndex = steps.findIndex((route) => route === SEEKER_CHILD_CARE_ROUTES.CARE_DATE);
    const kindIndex = steps.findIndex((route) => route === SEEKER_CHILD_CARE_ROUTES.CARE_KIND);
    const currentIndex = steps.findIndex((route) => route === path);

    if (seekerCCState.czenTimeIntent && currentIndex > dateIndex) {
      number -= 1;
    }
    if (seekerCCState.czenServiceIdForMember && currentIndex > kindIndex) {
      number -= 1;
    }
  } else if (isDCFlow(flowName)) {
    if (seekerCCState.czenTimeIntent) number -= 1;
    if (seekerCCState.czenServiceIdForMember) number -= 1;
  } else if (flowName === FLOWS.SEEKER_INSTANT_BOOK.name) {
    if (
      !seekerCCState.instantBook.isHomeAddress &&
      (path === SEEKER_INSTANT_BOOK_ROUTES.NAME || path === SEEKER_INSTANT_BOOK_ROUTES.LAST_STEP)
    ) {
      number += 1;
    }
  }

  return number;
};

const useFlowHelper = () => {
  const { asPath } = useRouter();
  const seekerCCState = useSeekerCCState();
  const { typeOfCare, dataTransferMetadata } = useSeekerState();
  const path = asPath.split('?')[0];
  const { flowName, comeFrom } = useFlowState();
  const dispatch = useAppDispatch();
  const { flags } = useFeatureFlags();
  const { isSeekerSinglePageEnrollment } = useIsSinglePageEnrollment();
  const { data: vhpSharedData, isFetching: isFetchingVhpData } = useRetrieveVhpMfeSharedData();
  const [hideStepNumber, setHideStepNumber] = useState(true);
  const { isAdultCareFlow } = useAdultCare();

  // https://carecom.atlassian.net/browse/PN-5779: daycare-enrollment-flow-reduce-v2 A/B test
  // index: 0 - reduced flow off - value = 0
  // index: 1 - reduced flow on - value = 1
  const inReducedFlowTest = flags?.[CLIENT_FEATURE_FLAGS.DAYCARE_REDUCED_FUNNEL_V2]?.value === 1;

  const isGrowthScHelpTypeScreenV1 =
    flags[CLIENT_FEATURE_FLAGS.GROWTH_SC_HELP_TYPE_SCREEN_V1]?.value === '2';

  let currentFlow = useMemo(
    () => findCurrentFlow(path, flowName, inReducedFlowTest),
    [path, flowName]
  );

  if (path === SEEKER_CHILD_CARE_ROUTES.INDEX || isFromVhpWithZip(comeFrom, dataTransferMetadata)) {
    currentFlow = flowName;
  }

  if (typeOfCare === SENIOR_CARE_TYPE.IN_FACILITY && currentFlow === 'SEEKER') {
    currentFlow = FLOWS.SEEKER_IN_FACILITY.name;
  }
  const totalSteps = useMemo(
    () => (currentFlow ? getStepsLength(seekerCCState, currentFlow, inReducedFlowTest) : 0),
    [currentFlow, seekerCCState, inReducedFlowTest]
  );
  const stepNumber = currentFlow ? getStepNumber(seekerCCState, currentFlow, path) : 0;

  const hideOnSCIndexPage =
    path === SEEKER_ROUTES.INDEX && !isGrowthScHelpTypeScreenV1 && !isAdultCareFlow;
  const hideProgressIndicator =
    isSeekerSinglePageEnrollment ||
    hideOnSCIndexPage ||
    (!path.includes('in-facility') && path.includes('recap')) ||
    (totalSteps === 0 && stepNumber === 0);

  const showBottomBorder = isSeekerSinglePageEnrollment || path.includes('/ib/recap');

  useEffect(() => {
    if (path.includes(FLOWS.SEEKER_INSTANT_BOOK_PET_CARE.path)) {
      currentFlow = FLOWS.SEEKER_INSTANT_BOOK_PET_CARE.name;
      dispatch({ type: 'setFlowName', flowName: FLOWS.SEEKER_INSTANT_BOOK_PET_CARE.name });
    }
  }, []);

  useEffect(() => {
    if (!isFetchingVhpData) {
      const isSeekerFlow = flowName.includes('SEEKER');

      if (isSeekerFlow && (vhpSharedData || comeFrom)) {
        const hasVhpSharedData = !comeFrom && vhpSharedData;

        setHideStepNumber(Boolean(isFromVhp(comeFrom) || hasVhpSharedData));
      } else {
        setHideStepNumber(
          path.indexOf(FLOWS.PROVIDER.path) >= 0 ||
            path === SEEKER_ROUTES.INDEX ||
            path.indexOf(FLOWS.SEEKER_IN_FACILITY.path) >= 0 ||
            currentFlow === FLOWS.SEEKER_IN_FACILITY.name ||
            currentFlow === FLOWS.LTCG.name ||
            currentFlow === FLOWS.SEEKER_INSTANT_BOOK_PET_CARE.name ||
            path.includes('recap') ||
            path.includes('match-daycare')
        );
      }
    }
  }, [isFetchingVhpData, path, currentFlow]);

  return {
    currentFlow,
    totalSteps,
    stepNumber,
    hideStepNumber,
    hideProgressIndicator,
    showBottomBorder,
  };
};

export default useFlowHelper;
